import { useState, useRef, useEffect } from 'react';
import { api, apiBlob } from './hrApi';
import { useAuth } from './hrAuth';

const RL = { admin: 'Admin', re: 'RE', support: 'Support', re_manager: 'RE Manager' };

export default function Profile() {
  const { user, enterApp } = useAuth();
  const [photoUrl, setPhotoUrl]   = useState(null);
  const [pw, setPw]               = useState('');
  const [pwMsg, setPwMsg]         = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState('');
  const [uploadOk, setUploadOk]   = useState(false);
  const [preview, setPreview]     = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (user.has_photo) {
      apiBlob('/photo').then(setPhotoUrl).catch(() => {});
    }
  }, [user.has_photo]);

  const onFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPreview(URL.createObjectURL(file));
    setUploadMsg('');
  };

  const doUpload = async () => {
    const file = fileRef.current?.files[0];
    if (!file) { setUploadMsg('Please select a photo first.'); return; }
    setUploading(true);
    setUploadMsg('Uploading…');
    setUploadOk(false);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const token = localStorage.getItem('access_token');
      if (!token) { setUploadMsg('Session expired. Please refresh.'); setUploading(false); return; }
      const r = await fetch('/api/kiosk/upload_photo', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Upload failed');
      setUploadMsg(data.message);
      setUploadOk(data.enrolled);
      // Refresh photo display
      setPhotoUrl(preview);
      // Update auth context with new employee payload
      if (data.employee) enterApp(data.employee);
    } catch (e) {
      setUploadMsg(e.message);
      setUploadOk(false);
    }
    setUploading(false);
  };

  const changePw = async () => {
    if (pw.length < 6) { setPwMsg('Minimum 6 characters.'); return; }
    try {
      await api('/change_password', { method: 'POST', body: JSON.stringify({ new_password: pw }) });
      setPwMsg('Updated ✓'); setPw('');
    } catch (e) { setPwMsg(e.message); }
  };

  const displayPhoto = preview || photoUrl;
  const initials = (user.first_name || user.employee_id || '?')[0].toUpperCase();

  const fields = [
    ['Name', user.name], ['Employee ID', user.employee_id], ['Email', user.email],
    ['Designation', user.designation], ['Department', user.department],
    ['Mobile', user.mobile], ['Date of joining', user.date_of_joining],
    ['Reporting to', user.reporting_to], ['Role', RL[user.role] || user.role],
  ];

  return (
    <section className="page on">
      <h2 className="pagetitle display">Profile</h2>
      <div className="pagesub">Synced from Zoho People on every login.</div>

      {/* Profile info card */}
      <div className="card profgrid">
        <div className="profphoto" style={{ position: 'relative', cursor: 'pointer' }}
          onClick={() => fileRef.current?.click()} title="Click to change photo">
          {displayPhoto
            ? <img src={displayPhoto} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
            : initials}
          <div style={{
            position: 'absolute', bottom: 0, right: 0,
            background: '#0E9F8A', borderRadius: '50%',
            width: 26, height: 26, display: 'flex', alignItems: 'center',
            justifyContent: 'center', boxShadow: '0 1px 4px rgba(0,0,0,.2)'
          }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
              <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/>
              <circle cx="12" cy="13" r="4"/>
            </svg>
          </div>
        </div>

        <div style={{ flex: 1 }}>
          {fields.map(([k, v]) => (
            <div key={k} className="drow">
              <span className="k">{k}</span>
              <span>{v || '—'}</span>
            </div>
          ))}
          <div className="drow">
            <span className="k">Face enrollment</span>
            <span className={`pill ${user.face_enrolled ? 'ok' : 'mut'}`}>
              {user.face_enrolled ? 'Enrolled ✓' : 'Not enrolled'}
            </span>
          </div>
        </div>
      </div>

      {/* Photo upload card */}
      <div className="card" style={{ marginTop: 16 }}>
        <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>Update profile photo</h3>
        <p style={{ fontSize: 13, color: '#64718A', marginBottom: 14 }}>
          Use a clear, front-facing photo. This will also enroll your face for check-in.
        </p>

        <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }}
          onChange={onFileChange} />

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          {preview && (
            <img src={preview} alt="preview"
              style={{ width: 64, height: 64, borderRadius: '50%', objectFit: 'cover',
                       border: '2px solid #0E9F8A' }} />
          )}
          <button className="ghost" onClick={() => fileRef.current?.click()}>
            {preview ? 'Change photo' : 'Select photo'}
          </button>
          {preview && (
            <button className="primary" onClick={doUpload} disabled={uploading}>
              {uploading ? 'Uploading…' : 'Save & Enroll face'}
            </button>
          )}
        </div>

        {uploadMsg && (
          <div style={{ marginTop: 10, fontSize: 13,
            color: uploadOk ? '#16A34A' : uploading ? '#64718A' : '#D97706' }}>
            {uploadMsg}
          </div>
        )}
      </div>

    </section>
  );
}
